import React, { useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Loading from "../../components/Loading/Loading"; 
import { MdCategory, MdFileDownload } from "react-icons/md";
import { useGlobalContext } from "../../context/stateContext";

const MemberPayments = ({
  member_id,
  incomeTypes,
  memberPayments,
  getMemberPayments,
  gettingPayments,
  memberName, // Optional: pass member name for better file naming
}) => {
  const { requestWithAuth } = useAuth();
  const {user} = useGlobalContext();
  const [addingPayment, setAddingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState({
    member: member_id, 
    income_type: "", 
    amount: "",
    date: null,
    notes: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSelectChange = (selectedOption, { name }) => { 
    setPaymentData((prevData) => ({
      ...prevData,
      [name]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleDateChange = (date) => {
    setPaymentData((prevData) => ({
      ...prevData,
      date: date,
    }));
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!paymentData.payment_type || !paymentData.amount || !paymentData.date) {
      toast.error("Please fill in all required payment fields.");
      return;
    }

    try {
      setAddingPayment(true);
      const formattedDate = paymentData.date
        ? paymentData.date.toISOString().split("T")[0]
        : null; 

      const payload = {
        ...paymentData,
        amount: parseFloat(paymentData.amount), 
        date: formattedDate,
      };

      
      if (!payload.member) {
        payload.member = member_id; 
      }

      await requestWithAuth(`/finance/member-payments/${member_id}/`, "POST", JSON.stringify(payload), {
        "Content-Type": "application/json"
      });

      toast.success("Payment recorded successfully!");
      setPaymentData({
        member: member_id, 
        income_type: "",
        amount: "",
        date: null,
        notes: "",
      }); 
      getMemberPayments(member_id);
    } catch (error) {
      
      toast.error(error.message || "Failed to record payment."); 
    } finally {
      setAddingPayment(false);
    }
  };

  // Export to Excel (CSV) function - no external library needed
  const exportToExcel = () => {
    if (!memberPayments || memberPayments.length === 0) {
      toast.warning("No payment data to export");
      return;
    }

    try {
      // Prepare CSV content
      let csvContent = "No.,Date,Payment Type,Amount (GHS),Notes\n";
      
      memberPayments.forEach((payment, index) => {
        const notes = payment.notes ? `"${payment.notes.replace(/"/g, '""')}"` : '""';
        csvContent += `${index + 1},${payment.date},"${payment.payment_type || 'Unknown Type'}",${payment.amount || 0},${notes}\n`;
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const fileName = memberName 
        ? `${memberName.replace(/\s+/g, '_')}_Payment_History_${date}.csv`
        : `Member_${member_id}_Payment_History_${date}.csv`;
      
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Payment history exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export payment history");
    }
  };

  const incomeTypeOptions = incomeTypes?.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  
  return (
    <div className="bg-white p-6 rounded-lg shadow-md mt-6">
      <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">Record New Payment</h3>
      <form onSubmit={handleSubmitPayment} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Payment Type */}
        <div className="flex flex-col">
          <label htmlFor="income_type" className="font-semibold text-gray-700 mb-2">
            Payment Type <span className="text-red-500">*</span>
          </label>
          <select name="payment_type" onChange={handleInputChange} id="" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200">
            <option value="">Select Income Type</option>
              {incomeTypes?.map((incomeType) => (
                <option value={incomeType.id} key={incomeType.id}>
                  {incomeType.name}
                </option>
              ))}
              {user?.role !== "Welfare Manager" && (
                <option value="Other">Other (Create New)</option>
              )}
          </select>
          
        </div>
        

        {/* Amount */}
        <div className="flex flex-col">
          <label htmlFor="amount" className="font-semibold text-gray-700 mb-2">
            Amount <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="amount"
            name="amount"
            value={paymentData.amount}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
            placeholder="e.g., 100.00"
            step="0.01"
            required
          />
        </div>

        {/* Date */}
        <div className="flex flex-col">
          <label htmlFor="date" className="font-semibold text-gray-700 mb-2">
            Date <span className="text-red-500">*</span>
          </label>
          <DatePicker
            id="date"
            selected={paymentData.date}
            onChange={handleDateChange}
            dateFormat="yyyy-MM-dd"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
            placeholderText="Select date"
            required
          />
        </div>
        {paymentData?.payment_type === "Other" && (
              <div className="space-y-2 bg-blue-50 p-4 rounded-lg border border-blue-200">
                <label className="flex items-center gap-2 text-sm font-medium text-blue-700">
                  <MdCategory className="text-blue-500" />
                  New Income Type Name
                </label>
                <input
                  type="text"
                  name="new_income_type"
                  id="new_income_type"
                  placeholder="Enter new income type name"
                  onChange={handleInputChange}
                  required={paymentData?.income_type === "Other"}
                  className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                <p className="text-xs text-blue-600">This will create a new income category for future use.</p>
              </div>
            )}

        {/* Notes */}
        <div className="flex flex-col md:col-span-2 lg:col-span-3">
          <label htmlFor="notes" className="font-semibold text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            value={paymentData.notes}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
            placeholder="Add any relevant notes or descriptions"
          ></textarea>
        </div>

        {/* Submit Button */}
        <div className="md:col-span-2 lg:col-span-3 flex justify-end">
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md shadow-md hover:shadow-lg transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            disabled={addingPayment}
          >
            {addingPayment ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Recording...
              </>
            ) : (
              "Record Payment"
            )}
          </button>
        </div>
      </form>

      <div className="border-t border-gray-200 my-8"></div> {/* Styled divider */}

      {/* Payment History Header with Export Button */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-800">Payment History</h3>
        <button
          onClick={exportToExcel}
          disabled={!memberPayments || memberPayments.length === 0}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md shadow-md hover:shadow-lg transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <MdFileDownload className="text-xl" />
          Export to Excel
        </button>
      </div>

      {gettingPayments ? (
        <Loading />
      ) : (
        <div className="overflow-x-auto shadow-md rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Payment Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Amount
                </th>
                
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {memberPayments?.length > 0 ? (
                memberPayments?.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {payment.payment_type || "Unknown Type"}
                      
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <span className="font-semibold text-green-600">
                        GHS{(payment?.amount || 0)}
                      </span>
                    </td>
                    
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    No payments recorded for this member yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MemberPayments;